import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, X, Copy, Mail, KeyRound, UserMinus, UserCheck, RotateCcw, Eye, ChevronDown, Wand2, Webhook } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
    pending: "bg-ink-100 text-ink-500 border-ink-300",
    active: "bg-haevn-50 text-haevn-700 border-haevn-100",
    suspended: "bg-clay-50 text-clay-500 border-clay-50",
    archived: "bg-ink-100 text-ink-400 border-ink-300",
};

const ROLE_BADGE = {
    admin: "bg-haevn-700 text-white",
    trainer: "bg-haevn-500 text-white",
    trainee: "bg-ink-200 text-ink-700",
};

const fld = "w-full bg-white border border-ink-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-haevn-700 transition-colors";
const lbl = "block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5";

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [filter, setFilter] = useState({ status: "all", role: "all", q: "" });
    const [showInvite, setShowInvite] = useState(false);
    const [showAllowlist, setShowAllowlist] = useState(false);
    const [link, setLink] = useState(null); // { kind, link, user }
    const [progressUser, setProgressUser] = useState(null);
    const [editUser, setEditUser] = useState(null);

    const load = () => api.get(`/admin/users?include_archived=${includeArchived}`).then((r) => setUsers(r.data));
    useEffect(() => { load(); }, [includeArchived]);
    useEffect(() => { api.get("/admin/groups").then((r) => setGroups(r.data)); }, []);

    const groupTitleBySlug = useMemo(() => {
        const m = {};
        groups.forEach((g) => { m[g.slug] = g.title; });
        return m;
    }, [groups]);

    const filtered = useMemo(() => {
        return users.filter((u) => {
            if (filter.status !== "all" && (u.status || "active") !== filter.status) return false;
            if (filter.role !== "all" && u.role !== filter.role) return false;
            if (filter.q) {
                const q = filter.q.toLowerCase();
                if (!u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q) && !(u.cohort || "").toLowerCase().includes(q)) return false;
            }
            return true;
        });
    }, [users, filter]);

    const stats = useMemo(() => {
        const s = { active: 0, pending: 0, suspended: 0, archived: 0 };
        users.forEach((u) => { s[u.status || "active"] = (s[u.status || "active"] || 0) + 1; });
        return s;
    }, [users]);

    const setStatus = async (u, status) => {
        try {
            await api.put(`/admin/users/${u.id}`, { status });
            toast.success(`Status updated to ${status}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const archive = async (u) => {
        if (!window.confirm(`Archive ${u.name}? They will lose access immediately.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User archived");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const sendReset = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
            setLink({ kind: "reset", link: data.reset_link, email_sent: data.email_sent, user: u });
        } catch (e) { toast.error("Failed"); }
    };

    const sendMagicLink = async (u) => {
        try {
            const { data } = await api.post(`/admin/allowlist/${u.id}/resend`);
            setLink({ kind: "magic", link: data.magic_link, email_sent: data.email_sent, user: u });
        } catch (e) { toast.error("Failed"); }
    };

    const replayWebhook = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/replay-webhook`);
            const st = data.training_completed_webhook_status;
            const err = data.training_completed_webhook_error;
            if (err) {
                toast.error(`Replay failed: ${err}`);
            } else if (st === 200) {
                toast.success(`Replayed → haevn.co returned 200 OK for ${u.email}`);
            } else {
                toast.warning(`Replayed. haevn.co returned HTTP ${st ?? "?"}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Replay failed");
        }
    };

    const replayAllWebhooks = async () => {
        if (!window.confirm("Re-fire the haevn.co training-complete webhook for EVERY certified Community Partner. Continue?")) return;
        try {
            const { data } = await api.post(`/admin/replay-all-webhooks`);
            const okN = (data.results || []).filter((r) => r.status === 200).length;
            const skipped = (data.results || []).filter((r) => r.skipped).length;
            const failed = (data.results || []).filter((r) => r.error || (r.status && r.status !== 200)).length;
            toast.success(`Replayed ${data.count}: ${okN} OK · ${failed} failed · ${skipped} skipped`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Batch replay failed");
        }
    };

    const resendInvite = async (u) => {
        try {
            const { data } = await api.post(`/admin/users/${u.id}/resend-invite`);
            setLink({ kind: "invite", link: data.invite_link, email_sent: data.email_sent, user: u });
            load();
        } catch (e) { toast.error("Failed"); }
    };

    return (
        <div className="space-y-6" data-testid="admin-users-page">
            {/* Header strip */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-300 border border-ink-300">
                <Stat label="Active" value={stats.active || 0} />
                <Stat label="Pending invites" value={stats.pending || 0} />
                <Stat label="Suspended" value={stats.suspended || 0} />
                <Stat label="Archived" value={stats.archived || 0} />
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
                <input
                    placeholder="Search name, email, cohort…"
                    value={filter.q}
                    onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                    data-testid="admin-users-search"
                    className={`${fld} max-w-xs`}
                />
                <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} className={fld} data-testid="admin-users-filter-status">
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                    <option value="archived">Archived</option>
                </select>
                <select value={filter.role} onChange={(e) => setFilter({ ...filter, role: e.target.value })} className={fld} data-testid="admin-users-filter-role">
                    <option value="all">All roles</option>
                    <option value="admin">Admin</option>
                    <option value="trainer">Trainer</option>
                    <option value="trainee">Trainee</option>
                </select>
                <label className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-ink-500 ml-2">
                    <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="admin-users-include-archived" />
                    Show archived
                </label>
                <div className="flex-1" />
                <button onClick={replayAllWebhooks} data-testid="admin-replay-all-webhooks-btn" className="inline-flex items-center gap-1.5 border border-ink-300 text-ink-700 px-3 py-2 rounded-sm text-xs font-mono uppercase tracking-[0.15em] hover:border-haevn-700 hover:text-haevn-700">
                    <Webhook size={12} /> Replay all partner webhooks
                </button>
                <button onClick={() => setShowAllowlist(true)} data-testid="admin-authorize-email-btn" className="inline-flex items-center gap-1.5 border border-haevn-700 text-haevn-700 px-4 py-2 rounded-sm text-sm hover:bg-haevn-50">
                    <Wand2 size={14} /> Authorize email
                </button>
                <button onClick={() => setShowInvite(true)} data-testid="admin-invite-user-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900">
                    <Plus size={14} /> Invite user
                </button>
            </div>

            {/* Table */}
            <div className="bg-white border border-ink-300">
                <div className="hidden md:grid md:grid-cols-[1.5fr_1fr_120px_120px_1.2fr_180px] px-6 py-3 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono border-b border-ink-200">
                    <div>User</div>
                    <div>Cohort</div>
                    <div>Role</div>
                    <div>Status</div>
                    <div>Groups</div>
                    <div className="text-right">Actions</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="px-6 py-10 text-sm text-ink-500 text-center" data-testid="admin-users-empty">No users match your filters.</div>
                ) : filtered.map((u) => (
                    <div key={u.id} className="px-6 py-4 grid md:grid-cols-[1.5fr_1fr_120px_120px_1.2fr_180px] gap-3 items-center border-b border-ink-200 last:border-0" data-testid={`admin-user-row-${u.id}`}>
                        <div className="min-w-0">
                            <div className="text-sm text-ink-900 font-medium truncate">{u.name}</div>
                            <div className="text-xs text-ink-500 truncate">{u.email}</div>
                        </div>
                        <div className="text-sm text-ink-700">{u.cohort || <span className="text-ink-400">—</span>}</div>
                        <div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-[0.15em] font-mono ${ROLE_BADGE[u.role] || "bg-ink-200 text-ink-700"}`}>{u.role}</span>
                        </div>
                        <div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-mono border ${STATUS_BADGE[u.status || "active"]}`}>{u.status || "active"}</span>
                        </div>
                        <div className="text-xs text-ink-700 flex flex-wrap gap-1">
                            {(u.group_ids?.length ?? 0) === 0 ? (
                                <span className="text-ink-400">—</span>
                            ) : u.group_ids.map((slug) => (
                                <span key={slug} className="inline-flex items-center px-2 py-0.5 rounded-sm bg-ink-100 text-ink-700 text-[10px] font-mono">
                                    {groupTitleBySlug[slug] || slug}
                                </span>
                            ))}
                        </div>
                        <div className="md:text-right">
                            <RowActions
                                u={u}
                                onEdit={() => setEditUser(u)}
                                onResetPwd={() => sendReset(u)}
                                onResendInvite={() => resendInvite(u)}
                                onSendMagicLink={() => sendMagicLink(u)}
                                onReplayWebhook={() => replayWebhook(u)}
                                onSuspend={() => setStatus(u, "suspended")}
                                onActivate={() => setStatus(u, "active")}
                                onArchive={() => archive(u)}
                                onProgress={() => setProgressUser(u)}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {showInvite && <InviteModal groups={groups} onClose={() => setShowInvite(false)} onCreated={(linkData) => { setShowInvite(false); setLink(linkData); load(); }} />}
            {showAllowlist && <AllowlistModal groups={groups} onClose={() => setShowAllowlist(false)} onCreated={(linkData) => { setShowAllowlist(false); setLink(linkData); load(); }} />}
            {editUser && <EditModal user={editUser} groups={groups} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
            {link && <LinkModal data={link} onClose={() => setLink(null)} />}
            {progressUser && <ProgressModal user={progressUser} onClose={() => setProgressUser(null)} />}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{label}</div>
            <div className="mt-2 font-heading text-3xl text-ink-900">{value}</div>
        </div>
    );
}

function RowActions({ u, onEdit, onResetPwd, onResendInvite, onSendMagicLink, onReplayWebhook, onSuspend, onActivate, onArchive, onProgress }) {
    const [open, setOpen] = useState(false);
    const status = u.status || "active";
    const isMagic = u.auth_method === "magic_link";
    const isPartner = u.external_partner_id && (u.group_ids || []).includes("community-partners");
    return (
        <div className="relative inline-block text-left">
            <button onClick={() => setOpen((v) => !v)} data-testid={`admin-user-actions-${u.id}`}
                className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900 px-2 py-1 border border-ink-300 rounded-sm">
                Actions <ChevronDown size={12} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 mt-1 w-56 bg-white border border-ink-300 z-20 shadow-sm py-1">
                        <Item icon={Eye} label="View progress" onClick={() => { setOpen(false); onProgress(); }} testId={`action-progress-${u.id}`} />
                        <Item icon={UserCheck} label="Edit details" onClick={() => { setOpen(false); onEdit(); }} testId={`action-edit-${u.id}`} />
                        {isMagic && status !== "archived" && (
                            <Item icon={Wand2} label="Send sign-in link" onClick={() => { setOpen(false); onSendMagicLink(); }} testId={`action-magic-link-${u.id}`} />
                        )}
                        {!isMagic && status === "pending" && (
                            <Item icon={Mail} label="Resend invite" onClick={() => { setOpen(false); onResendInvite(); }} testId={`action-resend-${u.id}`} />
                        )}
                        {!isMagic && status !== "pending" && (
                            <Item icon={KeyRound} label="Send reset link" onClick={() => { setOpen(false); onResetPwd(); }} testId={`action-reset-${u.id}`} />
                        )}
                        {isPartner && (
                            <Item icon={Webhook} label="Replay haevn.co webhook" onClick={() => { setOpen(false); onReplayWebhook(); }} testId={`action-replay-webhook-${u.id}`} />
                        )}
                        {status === "active" && (
                            <Item icon={UserMinus} label="Suspend" onClick={() => { setOpen(false); onSuspend(); }} testId={`action-suspend-${u.id}`} />
                        )}
                        {status === "suspended" && (
                            <Item icon={RotateCcw} label="Reactivate" onClick={() => { setOpen(false); onActivate(); }} testId={`action-activate-${u.id}`} />
                        )}
                        {status === "archived" && !isMagic && (
                            <Item icon={Mail} label="Re-invite" onClick={() => { setOpen(false); onResendInvite(); }} testId={`action-reinvite-${u.id}`} />
                        )}
                        <div className="border-t border-ink-200 my-1" />
                        <Item icon={UserMinus} label="Archive" onClick={() => { setOpen(false); onArchive(); }} danger testId={`action-archive-${u.id}`} />
                    </div>
                </>
            )}
        </div>
    );
}

function Item({ icon: Icon, label, onClick, danger, testId }) {
    return (
        <button onClick={onClick} data-testid={testId}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-ink-100 transition-colors ${danger ? "text-clay-500 hover:bg-clay-50" : "text-ink-700"}`}>
            <Icon size={14} strokeWidth={1.5} /> {label}
        </button>
    );
}

function GroupChecklist({ groups, selected, onChange, testIdPrefix }) {
    return (
        <div className="border border-ink-300 rounded-sm max-h-48 overflow-y-auto scrollbar-thin">
            {groups.length === 0 && <div className="p-3 text-xs text-ink-500">No groups defined yet.</div>}
            {groups.map((g) => (
                <label key={g.slug} className="flex items-start gap-2 px-3 py-2 hover:bg-ink-100 cursor-pointer text-sm border-b border-ink-200 last:border-0">
                    <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={selected.includes(g.slug)}
                        onChange={(e) => {
                            if (e.target.checked) onChange([...selected, g.slug]);
                            else onChange(selected.filter((s) => s !== g.slug));
                        }}
                        data-testid={`${testIdPrefix}-${g.slug}`}
                    />
                    <div className="min-w-0">
                        <div className="text-ink-900">{g.title}</div>
                        {g.description && <div className="text-[11px] text-ink-500 truncate">{g.description}</div>}
                    </div>
                </label>
            ))}
        </div>
    );
}

function InviteModal({ groups, onClose, onCreated }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [selectedGroups, setSelectedGroups] = useState([]);

    const submit = async (e) => {
        e.preventDefault();
        const f = e.target;
        setBusy(true); setError("");
        try {
            const { data } = await api.post("/admin/invite", {
                email: f.email.value,
                name: f.name.value,
                role: f.role.value,
                cohort: f.cohort.value || null,
                group_ids: selectedGroups,
            });
            onCreated({ kind: "invite", link: data.invite_link, email_sent: data.email_sent, user: data.user });
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };

    return (
        <ModalShell title="Invite a user" onClose={onClose} testId="admin-invite-modal">
            <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div><label className={lbl}>Full name</label><input className={fld} name="name" required data-testid="invite-name-input" placeholder="Avery Reyes" /></div>
                    <div><label className={lbl}>Email</label><input type="email" className={fld} name="email" required data-testid="invite-email-input" placeholder="avery@haevn.co" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={lbl}>Role</label>
                        <select className={fld} name="role" defaultValue="trainee" data-testid="invite-role-select">
                            <option value="trainee">Trainee</option>
                            <option value="trainer">Trainer</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div><label className={lbl}>Cohort / team</label><input className={fld} name="cohort" placeholder="Q2 ramp" data-testid="invite-cohort-input" /></div>
                </div>
                <div>
                    <label className={lbl}>Assign to groups</label>
                    <GroupChecklist groups={groups} selected={selectedGroups} onChange={setSelectedGroups} testIdPrefix="invite-group" />
                    <div className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono mt-1">
                        Groups define what this user sees in the sidebar and which modules they can access.
                    </div>
                </div>
                {error && <div className="text-xs text-clay-500 bg-clay-50 px-3 py-2 rounded-sm" data-testid="invite-error">{error}</div>}
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm hover:border-haevn-700">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="invite-submit-btn" className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 disabled:opacity-50">
                        {busy ? "Generating…" : "Generate invite link"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function EditModal({ user, groups, onClose, onSaved }) {
    const [selectedGroups, setSelectedGroups] = useState(user.group_ids || []);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        const f = e.target;
        setBusy(true);
        try {
            await api.put(`/admin/users/${user.id}`, {
                name: f.name.value,
                role: f.role.value,
                cohort: f.cohort.value || null,
                group_ids: selectedGroups,
            });
            toast.success("User updated");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Edit ${user.name}`} onClose={onClose} testId="admin-edit-user-modal">
            <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div><label className={lbl}>Full name</label><input className={fld} name="name" defaultValue={user.name} required data-testid="edit-user-name" /></div>
                    <div>
                        <label className={lbl}>Role</label>
                        <select className={fld} name="role" defaultValue={user.role} data-testid="edit-user-role">
                            <option value="trainee">Trainee</option>
                            <option value="trainer">Trainer</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                </div>
                <div><label className={lbl}>Cohort / team</label><input className={fld} name="cohort" defaultValue={user.cohort || ""} data-testid="edit-user-cohort" /></div>
                <div>
                    <label className={lbl}>Groups</label>
                    <GroupChecklist groups={groups} selected={selectedGroups} onChange={setSelectedGroups} testIdPrefix="edit-group" />
                    <div className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono mt-1">
                        A user with no groups will see no content (admins/trainers always see everything).
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm hover:border-haevn-700">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="edit-user-submit" className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 disabled:opacity-50">
                        {busy ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function AllowlistModal({ groups, onClose, onCreated }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [sendLink, setSendLink] = useState(true);

    const submit = async (e) => {
        e.preventDefault();
        const f = e.target;
        setBusy(true); setError("");
        try {
            const { data } = await api.post("/admin/allowlist", {
                email: f.email.value,
                name: f.name.value || null,
                role: f.role.value,
                group_ids: selectedGroups,
                send_link: sendLink,
            });
            onCreated({
                kind: "magic",
                link: data.magic_link,
                email_sent: data.email_sent,
                user: data.user,
            });
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };

    return (
        <ModalShell title="Authorize email" onClose={onClose} testId="admin-allowlist-modal">
            <p className="text-sm text-ink-500 mb-5">
                Authorize someone to sign in with a magic link — no password required. Just add their email, pick a group, and they&apos;ll receive a one-time sign-in link.
            </p>
            <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={lbl}>Email</label>
                        <input type="email" className={fld} name="email" required data-testid="allowlist-email-input" placeholder="user@haevn.co" autoFocus />
                    </div>
                    <div>
                        <label className={lbl}>Name <span className="lowercase text-ink-400">(optional)</span></label>
                        <input className={fld} name="name" data-testid="allowlist-name-input" placeholder="Avery Reyes" />
                    </div>
                </div>
                <div>
                    <label className={lbl}>Role</label>
                    <select className={fld} name="role" defaultValue="trainee" data-testid="allowlist-role-select">
                        <option value="trainee">Trainee</option>
                        <option value="trainer">Trainer</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
                <div>
                    <label className={lbl}>Assign to groups</label>
                    <GroupChecklist groups={groups} selected={selectedGroups} onChange={setSelectedGroups} testIdPrefix="allowlist-group" />
                    <div className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono mt-1">
                        Groups define the sidebar items and modules this user can access.
                    </div>
                </div>
                <label className="flex items-start gap-2 text-sm text-ink-700 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={sendLink}
                        onChange={(e) => setSendLink(e.target.checked)}
                        className="mt-0.5"
                        data-testid="allowlist-send-link-checkbox"
                    />
                    <span>Email the sign-in link now (expires in 15 minutes). Uncheck to authorize silently — they can request a link themselves from the login screen.</span>
                </label>
                {error && <div className="text-xs text-clay-500 bg-clay-50 px-3 py-2 rounded-sm" data-testid="allowlist-error">{error}</div>}
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm hover:border-haevn-700">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="allowlist-submit-btn" className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 disabled:opacity-50">
                        {busy ? "Authorizing…" : (sendLink ? "Authorize & email link" : "Authorize")}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function LinkModal({ data, onClose }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        if (!data.link) return;
        navigator.clipboard.writeText(data.link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
    };
    const isInvite = data.kind === "invite";
    const isMagic = data.kind === "magic";
    const sent = data.email_sent;
    const title = isMagic
        ? (sent ? "Sign-in link sent" : "Email authorized")
        : isInvite ? "Invite sent" : "Reset email sent";
    return (
        <ModalShell title={title} onClose={onClose} testId="link-modal">
            {sent ? (
                <p className="text-sm text-ink-500 mb-4">
                    {isMagic
                        ? <>A one-time sign-in link was emailed to <span className="text-ink-900">{data.user?.email}</span>. It expires in 15 minutes.</>
                        : isInvite
                            ? <>An invitation email was sent to <span className="text-ink-900">{data.user?.email}</span>. They&rsquo;ll click the link, set a password, and the account becomes active.</>
                            : <>A password reset email was sent to <span className="text-ink-900">{data.user?.email}</span>. The link expires in 24 hours.</>}
                </p>
            ) : isMagic && !data.link ? (
                <p className="text-sm text-ink-500 mb-4">
                    <span className="text-ink-900">{data.user?.email}</span> is now authorized. They can sign in by entering their email on the login page — we&apos;ll email them a one-time link.
                </p>
            ) : (
                <p className="text-sm text-clay-500 mb-4">
                    Email could not be delivered. Share the link below directly with <span className="text-ink-900">{data.user?.email}</span> as a fallback.
                </p>
            )}
            {data.link && (
                <>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono mb-2">
                        {sent ? "Backup link (email already sent)" : "Manual link"}
                    </div>
                    <div className="bg-ink-100 border border-ink-300 rounded-sm p-3 text-xs font-mono break-all" data-testid="generated-link">
                        {data.link}
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                        <button onClick={copy} data-testid="copy-link-btn" className="inline-flex items-center gap-1.5 bg-haevn-700 text-white px-4 py-2 rounded-sm text-sm hover:bg-haevn-900">
                            <Copy size={14} /> {copied ? "Copied!" : "Copy link"}
                        </button>
                        <button onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm hover:border-haevn-700">Done</button>
                    </div>
                </>
            )}
            {!data.link && (
                <div className="mt-4">
                    <button onClick={onClose} className="border border-ink-300 px-4 py-2 rounded-sm text-sm hover:border-haevn-700">Done</button>
                </div>
            )}
            <div className="mt-4 text-[10px] uppercase tracking-[0.15em] text-ink-400 font-mono">
                {sent
                    ? "Sent via Resend from university@updates.haevn.co"
                    : data.link
                        ? "Resend delivery failed — check your Resend dashboard"
                        : "No link sent — user can self-serve from /login"}
            </div>
        </ModalShell>
    );
}

function ProgressModal({ user, onClose }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/admin/trainee-progress/${user.id}`).then((r) => setData(r.data));
    }, [user.id]);
    return (
        <ModalShell title={`${user.name} — progress`} onClose={onClose} testId="progress-modal" wide>
            {!data ? (
                <div className="text-sm text-ink-500">Loading…</div>
            ) : (
                <div className="space-y-6">
                    <div className="text-sm">
                        <div className="text-ink-500">Email <span className="text-ink-900">{data.user.email}</span></div>
                        <div className="text-ink-500">Cohort <span className="text-ink-900">{data.user.cohort || "—"}</span></div>
                        <div className="text-ink-500">Status <span className="text-ink-900 capitalize">{data.user.status || "active"}</span></div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-2">Progress entries</div>
                        <div className="bg-ink-50 border border-ink-300 max-h-48 overflow-y-auto scrollbar-thin">
                            {data.progress.length === 0 ? <div className="p-3 text-sm text-ink-500">None yet.</div> :
                                data.progress.slice(0, 50).map((p, i) => (
                                    <div key={i} className="px-3 py-2 text-xs border-b border-ink-200 flex justify-between">
                                        <span className="text-ink-700">{p.kind || "lesson"} · {p.completed ? "✓ done" : "in progress"}</span>
                                        <span className="text-ink-400 font-mono">{p.updated_at?.slice(0, 16)}</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono mb-2">Quiz attempts</div>
                        <div className="bg-ink-50 border border-ink-300 max-h-48 overflow-y-auto scrollbar-thin">
                            {data.attempts.length === 0 ? <div className="p-3 text-sm text-ink-500">None yet.</div> :
                                data.attempts.map((a) => (
                                    <div key={a.id} className="px-3 py-2 text-xs border-b border-ink-200 flex justify-between">
                                        <span className="text-ink-700">{a.correct}/{a.total} · {a.score}%</span>
                                        <span className={`font-mono ${a.passed ? "text-haevn-700" : "text-clay-500"}`}>{a.passed ? "PASSED" : "RETRY"}</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </ModalShell>
    );
}

function ModalShell({ title, onClose, children, testId, wide }) {
    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className={`bg-white border border-ink-300 ${wide ? "max-w-3xl" : "max-w-2xl"} w-full max-h-[90vh] overflow-y-auto p-8`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
                <div className="flex items-start justify-between mb-6">
                    <h2 className="font-editorial text-2xl">{title}</h2>
                    <button onClick={onClose} className="text-ink-400 hover:text-ink-900"><X size={18} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
